// @ts-nocheck
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { invalidateApiCache } from '../api/client'
import { useAuth } from '../auth/useAuth'
import { getVisibleMarketSources } from '../exchanges/exchangeAvailability'
import { useAdaptivePolling } from '../hooks/useAdaptivePolling'
import { MARKETPLACES_STATUS_CACHE_KEY, getMarketplacesStatus } from '../services/marketplacesApi'
import { buildIntegrationSync, type IntegrationSyncSnapshot } from './integrationSync'
import { INTEGRATION_SYNC_UPDATED_EVENT } from './integrationSyncEvents'
import { MarketStatusContext } from './marketStatusContextRef'
import { MARKET_SOURCES, buildDefaultMarketStatus } from './marketSources'

// 120 s: diody zmieniają się rzadko (token Trans, konfiguracja), a ten poller (30 s × 5 żądań
// × ~20 otwartych kart, także ukrytych) był 80,7% ruchu /api/* — 95 895 żądań/dobę 17.09.2026.
export const MARKET_STATUS_POLL_INTERVAL_MS = 120000

export function MarketStatusProvider({ token, children, pollIntervalMs = MARKET_STATUS_POLL_INTERVAL_MS }) {
  const { profile } = useAuth()
  const [marketStatus, setMarketStatus] = useState(buildDefaultMarketStatus)
  const [loadingSources, setLoadingSources] = useState({})
  const [statusError, setStatusError] = useState('')
  const [integrationSync, setIntegrationSync] = useState<Record<string, IntegrationSyncSnapshot>>({})
  const [integrationSyncLoading, setIntegrationSyncLoading] = useState(false)

  const abortRef = useRef(null)
  const requestIdRef = useRef(0)

  const visibleSources = useMemo(
    () => (profile ? getVisibleMarketSources(profile, MARKET_SOURCES) : []),
    [profile]
  )

  const stopRequest = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort()
      abortRef.current = null
    }
  }, [])

  const refreshStatus = useCallback(async () => {
    if (!token) {
      stopRequest()
      setMarketStatus(buildDefaultMarketStatus())
      setLoadingSources({})
      setStatusError('')
      setIntegrationSync({})
      setIntegrationSyncLoading(false)
      return
    }

    stopRequest()
    const controller = new AbortController()
    abortRef.current = controller

    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId

    setLoadingSources(Object.fromEntries(MARKET_SOURCES.map((source) => [source, true])))
    setIntegrationSyncLoading(true)

    try {
      const data = await getMarketplacesStatus(token, controller.signal)
      if (requestIdRef.current !== requestId) return
      setMarketStatus({
        lastSyncAt: data?.lastSyncAt || null,
        sources: data?.sources || {},
      })
      setIntegrationSync(profile ? buildIntegrationSync(data?.integrations, visibleSources) : {})
      setStatusError('')
    } catch (error) {
      if (error?.name === 'AbortError') return
      if (requestIdRef.current !== requestId) return
      setStatusError('status')
      setIntegrationSync({})
    } finally {
      if (requestIdRef.current === requestId) {
        setLoadingSources({})
        setIntegrationSyncLoading(false)
      }
    }
  }, [profile, stopRequest, token, visibleSources])

  // Ładowanie początkowe — useAdaptivePolling planuje pierwszy tik dopiero po pollIntervalMs.
  useEffect(() => {
    void refreshStatus()
  }, [refreshStatus])

  // Kolejne tiki: pauza w ukrytej karcie, natychmiastowe odświeżenie po powrocie, dedup w locie.
  const getDelayMs = useCallback(() => pollIntervalMs, [pollIntervalMs])
  useAdaptivePolling({ enabled: Boolean(token), getDelayMs, refresh: refreshStatus })

  useEffect(() => {
    const handleIntegrationSyncUpdated = () => {
      // Po zapisie integracji odpowiedź nie może przyjść z 15-sekundowego cache'u klienta.
      invalidateApiCache(MARKETPLACES_STATUS_CACHE_KEY, token)
      void refreshStatus()
    }

    window.addEventListener(INTEGRATION_SYNC_UPDATED_EVENT, handleIntegrationSyncUpdated)
    return () => {
      window.removeEventListener(INTEGRATION_SYNC_UPDATED_EVENT, handleIntegrationSyncUpdated)
    }
  }, [refreshStatus, token])

  useEffect(() => () => stopRequest(), [stopRequest])

  const value = useMemo(
    () => ({
      marketStatus,
      loadingSources,
      statusError,
      refreshStatus,
      integrationSync,
      integrationSyncLoading,
      // Zgodność wstecz: dawniej osobne odświeżenie 4 endpointów integracji — dziś ten sam strzał.
      refreshIntegrationSync: refreshStatus,
    }),
    [integrationSync, integrationSyncLoading, loadingSources, marketStatus, refreshStatus, statusError]
  )

  return <MarketStatusContext.Provider value={value}>{children}</MarketStatusContext.Provider>
}
